import { useState, useEffect, useCallback } from "react";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import {
	useGetPipedriveListSyncTriggerQuery,
	useUpdatePipedriveListSyncTriggerMutation,
	useGetPipedriveOAuthStatusQuery,
} from "@/pages/Private/redux/pipedrive/pipedrive.api";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import { PipedriveSyncTriggerWithInherit } from "../../../../Company/schema/pipedrive";

interface UsePipedriveSyncTriggerOptions {
	listId: number;
}

export const usePipedriveSyncTrigger = ({ listId }: UsePipedriveSyncTriggerOptions) => {
	const isPipedriveEnabled = useIsFeatureActive(FeaturesCodes.PIPEDRIVE_CRM);
	const { data: oauthStatus } = useGetPipedriveOAuthStatusQuery(undefined, {
		skip: !isPipedriveEnabled,
	});
	const isConnected = oauthStatus?.connected ?? false;

	const { data: listSyncTrigger } = useGetPipedriveListSyncTriggerQuery(listId, {
		skip: !isPipedriveEnabled || !isConnected || !listId,
	});
	const [updateListSyncTrigger] = useUpdatePipedriveListSyncTriggerMutation();

	const [selectedPipedriveSyncTrigger, setSelectedPipedriveSyncTrigger] = useState<
		PipedriveSyncTriggerWithInherit | undefined
	>(undefined);

	useEffect(() => {
		if (listSyncTrigger) {
			setSelectedPipedriveSyncTrigger(listSyncTrigger.trigger);
		}
	}, [listSyncTrigger]);

	const hasSyncTriggerChanged = useCallback(() => {
		return (
			isPipedriveEnabled &&
			isConnected &&
			selectedPipedriveSyncTrigger !== undefined &&
			selectedPipedriveSyncTrigger !== listSyncTrigger?.trigger
		);
	}, [isPipedriveEnabled, isConnected, selectedPipedriveSyncTrigger, listSyncTrigger?.trigger]);

	const saveSyncTrigger = useCallback(async () => {
		if (hasSyncTriggerChanged() && selectedPipedriveSyncTrigger) {
			await updateListSyncTrigger({
				listId,
				data: { trigger: selectedPipedriveSyncTrigger },
			}).unwrap();
		}
	}, [hasSyncTriggerChanged, selectedPipedriveSyncTrigger, listId, updateListSyncTrigger]);

	return {
		isPipedriveEnabled,
		isConnected,

		listSyncTrigger,
		selectedPipedriveSyncTrigger,
		setSelectedPipedriveSyncTrigger,

		hasSyncTriggerChanged,
		saveSyncTrigger,
	};
};
